import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import Modal from "../Modal/Modal";
import { Button } from "../Button";
import { Input } from "../Input";
import ColorPicker from "../ColorPicker/ColorPicker";
import { ImageUploadWithCrop } from "../Upload/ImageUploadWithCrop";
import { useTheme } from "../../contexts/Theme/ThemeContext";
import { variacaoValoresApi } from "../../hooks/variacaoValoresApi";
import { validateHex } from "../../services/MasksValidations";
import { ValorVariacao } from "../../types/Produto";
import { ReactComponent as Voltar } from "../../assets/newImages/icons/angle-down.svg";
import { ReactComponent as GaleryIcon } from "../../assets/newImages/icons/galery.svg";
import { ReactComponent as PlayIcon } from "../../assets/newImages/icons/play.svg";
import { ContainerModalEditarVariacao, ModalCroppingContainer } from "./styles";

type ModoSwatch = "cor" | "foto" | "nenhum";

/** Cor que o picker mostra quando ainda não há nenhuma escolhida. */
const COR_PADRAO = "#FFFFFF";

interface ModalEditarVariacaoProps {
  isOpen: boolean;
  setModal: (value: boolean) => void;
  /** Tipo ao qual o valor pertence (ex.: "Cor"). O swatch é salvo por (empresa, tipo, valor). */
  idTipo: number;
  /** Nome atual do valor, como está no produto. */
  valor: string;
  /** Swatch já cadastrado para esse valor, se houver. */
  valorCadastrado?: ValorVariacao | null;
  /**
   * Renomear é LOCAL ao produto: mexe só na string de produto_variacoes.
   * Renomear "Azul" para "Azul Marinho" cria um valor diferente, com swatch próprio.
   */
  onAplicar: (novoNome: string, swatch: ValorVariacao | null) => void;
  permitirRenomear?: boolean;
}

export const ModalEditarVariacao: React.FC<ModalEditarVariacaoProps> = ({
  isOpen,
  setModal,
  idTipo,
  valor,
  valorCadastrado,
  onAplicar,
  permitirRenomear = true,
}) => {
  const { theme } = useTheme();
  const [nome, setNome] = useState("");
  const [modo, setModo] = useState<ModoSwatch>("nenhum");
  const [cor, setCor] = useState("");
  const [imagem, setImagem] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [arrastando, setArrastando] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setNome(valor);
    setCor(valorCadastrado?.cor_hex || "");
    setImagem(valorCadastrado?.imagem || "");
    // Abre no que está salvo. Variação sem swatch abre em "nenhum", para que
    // renomear não exija escolher uma cor que o usuário não quer.
    setModo(
      valorCadastrado?.imagem ? "foto" : valorCadastrado?.cor_hex ? "cor" : "nenhum"
    );
    setSalvando(false);
  }, [isOpen, valor, valorCadastrado]);

  const tinhaSwatch = !!(valorCadastrado?.cor_hex || valorCadastrado?.imagem);

  // Entrar em "cor" já grava COR_PADRAO no ESTADO, e não só no que o picker mostra:
  // aplicar sem encostar no picker salva o branco que está à vista, em vez de cair na
  // validação de "escolha uma cor". Daí em diante o estado é a única fonte — o campo
  // aceita ficar vazio enquanto o usuário digita outro hex.
  const escolherModo = (novoModo: ModoSwatch) => {
    if (novoModo === "cor" && !cor) setCor(COR_PADRAO);
    setModo(novoModo);
  };

  const handleAplicar = async () => {
    const nomeFinal = permitirRenomear ? nome.trim() : valor.trim();

    if (!nomeFinal) {
      return Swal.fire({
        title: "Ops!",
        text: "Informe o nome da variação.",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    // Radio: só vai o que corresponde ao modo escolhido. O outro sai vazio e o
    // backend limpa a coluna, então trocar de cor para foto não deixa resíduo.
    // Em "nenhum" os dois vão vazios, que é como o backend apaga o swatch.
    const cor_hex = modo === "cor" ? cor : "";
    const foto = modo === "foto" ? imagem : "";

    // Modo escolhido sem preencher apagaria o swatch de todos os produtos sem o
    // usuário pedir. Quem quer apagar tem "Não usar cor ou foto".
    // Hex pela metade ("#4A") também não passa: salvo assim, o swatch sairia sem cor
    // no catálogo.
    if (modo === "cor" && !validateHex(cor_hex)) {
      return Swal.fire({
        title: "Ops!",
        text: 'Informe o código da cor com os 6 dígitos (ex.: #4608AD) ou marque "Não usar cor ou foto".',
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    if (modo === "foto" && !foto) {
      return Swal.fire({
        title: "Ops!",
        text: 'Envie uma imagem ou marque "Não usar cor ou foto".',
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    // Apagar é global igual a cadastrar, então confirma antes.
    if (modo === "nenhum" && tinhaSwatch) {
      const confirmacao = await Swal.fire({
        icon: "warning",
        title: "Remover a cor ou imagem desta variação?",
        text: `"${valor}" volta a ser exibida apenas como texto em todos os produtos que usam essa variação.`,
        showCancelButton: true,
        confirmButtonText: "Remover",
        cancelButtonText: "Cancelar",
        customClass: { confirmButton: "btn-confirm", icon: "custom-swal-icon" },
      });

      if (!confirmacao.isConfirmed) return;
    }

    setSalvando(true);
    try {
      const swatch = await variacaoValoresApi().salvarValor({
        id_tipo: idTipo,
        valor: nomeFinal,
        cor_hex,
        imagem: foto,
      });
      onAplicar(nomeFinal, swatch);
      setModal(false);
    } catch (error) {
      console.error("Erro ao salvar variação:", error);
      Swal.fire({
        title: "Erro!",
        text: "Não foi possível salvar a variação.",
        icon: "error",
        confirmButtonText: "Ok",
      });
    } finally {
      setSalvando(false);
    }
  };

  // Mesmo radio nativo do ajuste de preço do catálogo: a classe global
  // .input-radio (app_styles.ts) tinge o controle do sistema com a cor primária.
  const opcao = (valorModo: ModoSwatch, label: string) => (
    <label className="opcao fonteTexto">
      <input
        type="radio"
        className="input-radio"
        name="modoSwatch"
        checked={modo === valorModo}
        onChange={() => escolherModo(valorModo)}
      />
      <span>{label}</span>
    </label>
  );

  return (
    <Modal isOpen={isOpen} setModal={setModal} width="500px" maxWidth="90vw">
      <ContainerModalEditarVariacao theme={theme}>
        <div className="titulo fonteTitulo">Editar variação</div>
        <div className="subtitulo fonteTexto">
          {permitirRenomear
            ? "Edite as informações e adicione uma foto ou cor à variação. "
            : "Adicione uma foto ou cor à variação. "}
          <br />
          <strong>
            Mas atenção: a cor ou imagem adicionada à variação será apresentada
            toda vez que essa variação for utilizada nos produtos.
          </strong>
        </div>

        <div className="campo">
          <Input
            label="Nome da variação"
            type="text"
            name="nome_variacao"
            value={nome}
            maxLength={255}
            placeholder="Nome"
            disabled={!permitirRenomear}
            onChange={(newValue: string) => setNome(newValue)}
          />
          {!permitirRenomear && (
            <div className="aviso fonteRotulos">
              O nome vem da sua integração e é atualizado por ela. Aqui você define
              apenas a cor ou a foto da variação.
            </div>
          )}
        </div>

        <div className="pergunta fonteRotulos">
          Escolha a cor ou imagem que deseja adicionar a variação:
        </div>

        <div className="opcoes">
          {opcao("cor", "Selecionar cor")}
          {opcao("foto", "Adicionar fotografia")}
          {opcao("nenhum", "Não usar cor ou foto")}
        </div>

        {modo === "cor" && (
          <div className="color-wrapper">
            <ColorPicker
              color={cor}
              onChange={(novaCor: any) => setCor(novaCor.hex)}
              onChangeInput={(novaCor: string) => setCor(novaCor)}
              mostrarPreview
            />
          </div>
        )}

        {modo === "foto" && (
          <div className="upload-wrapper">
            <ImageUploadWithCrop
              onLoading={() => {}}
              className="swatch-upload-label"
              acceptFormats=".png, .jpeg, .jpg, .webp, .heic, .heif"
              value={imagem}
              aspectRatio={1 / 1}
              circularCrop={true}
              resize={{ width: 150, height: 150 }}
              minWidth={150}
              minHeight={150}
              errorMargin={10}
              onChange={(base64: string) => setImagem(base64)}
              onDragIn={() => setArrastando(true)}
              onDragOut={() => setArrastando(false)}
              onDragDrop={() => setArrastando(false)}
              onOpenCrop={(cropping, cancelar, salvar) => (
                <Modal isOpen={true} setModal={() => {}} width="auto" padding="5rem 5rem">
                  <ModalCroppingContainer theme={theme}>
                    <div className="titulo fonteTexto">Editar imagem</div>
                    {cropping}
                    <div className="botoes">
                      {cancelar}
                      {salvar}
                    </div>
                  </ModalCroppingContainer>
                </Modal>
              )}
            >
              <div className="swatch-dropzone">
                {imagem ? (
                  <img className="preview" src={imagem} alt={nome} />
                ) : (
                  <>
                    <div className="icones">
                      <GaleryIcon
                        className={`galery-icon ${arrastando ? "galery-ativo" : ""}`}
                      />
                      <PlayIcon
                        className={`play-icon ${arrastando ? "play-ativo" : ""}`}
                      />
                    </div>
                    <div className="texto-wrapper">
                      <div
                        className="texto fonteRotulos"
                        style={{ opacity: arrastando ? 0 : 1 }}
                      >
                        Clique aqui ou arraste e solte o arquivo
                      </div>
                      <div
                        className="texto fonteRotulos"
                        style={{ opacity: arrastando ? 1 : 0 }}
                      >
                        Solte o arquivo aqui
                      </div>
                    </div>
                  </>
                )}
              </div>
            </ImageUploadWithCrop>
          </div>
        )}

        <div className="botoes">
          <div className="cancelar fonteTexto" onClick={() => setModal(false)}>
            <Voltar /> Cancelar
          </div>
          <Button
            title="Aplicar"
            className="fonteTexto"
            loading={salvando}
            onClick={handleAplicar}
            disabled={!nome.trim()}
          />
        </div>
      </ContainerModalEditarVariacao>
    </Modal>
  );
};

export default ModalEditarVariacao;
